<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { postScheduleAction } from 'dashboard/api/scheduleActions';
import { messageTimestamp } from 'shared/helpers/timeHelper';

import BaseBubble from './Base.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'next/icon/Icon.vue';

import { useMessageContext } from '../provider.js';

const { t } = useI18n();
const route = useRoute();

const { id, content, contentAttributes } = useMessageContext();

const currentUser = useMapGetter('getCurrentUser');
const currentUserId = computed(() => currentUser.value?.id);

const conversationDisplayId = computed(
  () => route.params.conversation_id ?? route.params.conversationId
);

const editedText = computed(() => contentAttributes.value?.editedText);
const displayText = computed(() => editedText.value || content.value || '');
const scheduledAt = computed(() => contentAttributes.value?.scheduledAt);
const scheduleState = computed(
  () => contentAttributes.value?.scheduleState || 'pending'
);

// Keep in sync with ScheduleMessages::TransitionService TERMINAL_STATES /
// FAILURE_STATES (transition_service.rb) — drift shows action buttons on dead
// schedules and the API 409s.
const TERMINAL_STATES = [
  'sent',
  'rejected',
  'cancelled',
  'failed_window_closed',
  'failed_send_error',
  'failed_conversation_resolved',
];
const FAILURE_STATES = [
  'failed_window_closed',
  'failed_send_error',
  'failed_conversation_resolved',
];
const isTerminal = computed(() =>
  TERMINAL_STATES.includes(scheduleState.value)
);
const isFailure = computed(() => FAILURE_STATES.includes(scheduleState.value));

// Each state gets its own visual treatment. Available radix ramps with both
// step-3 and step-11 tokens: amber, blue, iris, ruby, slate, teal, violet.
// To hit 9 distinct treatments we vary saturation (step-3 soft vs step-9
// solid) where two states share a hue family.
const STATE_PILL_CLASSES = {
  pending: 'bg-n-slate-3 text-n-slate-11',
  edited: 'bg-n-amber-3 text-n-amber-11',
  approved: 'bg-n-teal-3 text-n-teal-11',
  sent: 'bg-n-teal-9 text-white',
  rejected: 'bg-n-ruby-9 text-white',
  cancelled: 'bg-n-slate-9 text-white',
  failed_window_closed: 'bg-n-amber-9 text-white',
  failed_send_error: 'bg-n-ruby-3 text-n-ruby-11',
  failed_conversation_resolved: 'bg-n-violet-3 text-n-violet-11',
};
const statePillClass = computed(
  () => STATE_PILL_CLASSES[scheduleState.value] || STATE_PILL_CLASSES.pending
);

// One icon per state — used inside the state pill in the header so the
// pill carries both glyph + label.
const STATE_ICONS = {
  pending: 'i-lucide-clock',
  edited: 'i-lucide-pencil',
  approved: 'i-lucide-check-circle',
  sent: 'i-lucide-check',
  rejected: 'i-lucide-x-circle',
  cancelled: 'i-lucide-x',
  failed_window_closed: 'i-lucide-alert-triangle',
  failed_send_error: 'i-lucide-alert-triangle',
  failed_conversation_resolved: 'i-lucide-alert-triangle',
};
const stateIcon = computed(
  () => STATE_ICONS[scheduleState.value] || STATE_ICONS.pending
);

const failureReasonKey = computed(() => {
  switch (scheduleState.value) {
    case 'failed_window_closed':
      return 'SCHEDULE_BUBBLE.FAILURE_REASON.WINDOW_CLOSED';
    case 'failed_send_error':
      return 'SCHEDULE_BUBBLE.FAILURE_REASON.SEND_ERROR';
    case 'failed_conversation_resolved':
      return 'SCHEDULE_BUBBLE.FAILURE_REASON.CONVERSATION_RESOLVED';
    default:
      return null;
  }
});

// Live countdown
const now = ref(Date.now());
let tickHandle = null;
onMounted(() => {
  // Terminal and untimed schedules never render a countdown — skip the tick.
  if (isTerminal.value || !scheduledAt.value) return;
  tickHandle = window.setInterval(() => {
    now.value = Date.now();
  }, 1000);
});
onBeforeUnmount(() => {
  if (tickHandle) window.clearInterval(tickHandle);
});

const countdownLabel = computed(() => {
  if (!scheduledAt.value) return '';
  const remaining = Math.max(
    0,
    new Date(scheduledAt.value).getTime() - now.value
  );
  if (remaining === 0) return t('SCHEDULE_BUBBLE.COUNTDOWN_DUE');
  const totalSec = Math.floor(remaining / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
});

// Polite (minute-granularity) version for aria-live so screen readers
// don't get spammed every second.
const politeCountdownLabel = computed(() => {
  if (!scheduledAt.value) return '';
  const remaining = Math.max(
    0,
    new Date(scheduledAt.value).getTime() - now.value
  );
  if (remaining === 0) return t('SCHEDULE_BUBBLE.COUNTDOWN_DUE');
  const totalMin = Math.ceil(remaining / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
});

// Edit mode
const editing = ref(false);
const editDraft = ref('');
const busy = ref(false);
const textareaRef = ref(null);

const adjustTextareaHeight = () => {
  if (!textareaRef.value) return;
  textareaRef.value.style.height = 'auto';
  textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`;
};

const startEdit = () => {
  editing.value = true;
  editDraft.value = displayText.value;
  nextTick(adjustTextareaHeight);
};
const cancelEdit = () => {
  editing.value = false;
};

const generateActionId = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `sa-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const post = async (transitionAction, extra = {}) => {
  busy.value = true;
  try {
    await postScheduleAction({
      conversationDisplayId: conversationDisplayId.value,
      messageId: id.value,
      transitionAction,
      actionId: generateActionId(),
      agentId: currentUserId.value,
      ...extra,
    });
  } catch (err) {
    const status = err?.response?.status;
    const errorBody = err?.response?.data?.error;
    let messageKey;
    if (status === 409) {
      messageKey = 'SCHEDULE_BUBBLE.ERRORS.CONFLICT';
    } else if (status === 422 && errorBody === 'conversation_resolved') {
      messageKey = 'SCHEDULE_BUBBLE.ERRORS.CONVERSATION_RESOLVED';
    } else if (status === 422) {
      messageKey = 'SCHEDULE_BUBBLE.ERRORS.INVALID';
    } else {
      messageKey = 'SCHEDULE_BUBBLE.ERRORS.UNKNOWN';
    }
    useAlert(t(messageKey, { detail: errorBody || '' }));
  } finally {
    busy.value = false;
  }
};

const saveEdit = async () => {
  await post('edit', { editedText: editDraft.value });
  editing.value = false;
};

// Confirmation dialog
const dialogRef = ref(null);
const pendingAction = ref(null);

const CONFIRM_CONFIG = {
  reject: { type: 'alert', key: 'REJECT', color: 'ruby' },
  approve: { type: 'edit', key: 'APPROVE', color: 'teal' },
  send_now: { type: 'edit', key: 'SEND_NOW', color: 'blue' },
};

const confirmCopy = computed(() => {
  const cfg = pendingAction.value && CONFIRM_CONFIG[pendingAction.value];
  if (!cfg)
    return {
      type: 'alert',
      title: '',
      description: '',
      confirmLabel: '',
      confirmColor: '',
    };
  const base = `SCHEDULE_BUBBLE.CONFIRM.${cfg.key}`;
  return {
    type: cfg.type,
    title: t(`${base}.TITLE`),
    description: t(`${base}.DESCRIPTION`),
    confirmLabel: t(`${base}.CONFIRM`),
    confirmColor: cfg.color,
  };
});

const requestConfirm = action => {
  pendingAction.value = action;
  dialogRef.value?.open();
};

const onConfirm = async () => {
  const action = pendingAction.value;
  dialogRef.value?.close();
  await post(action);
};

const approve = () => requestConfirm('approve');
const reject = () => requestConfirm('reject');
const sendNow = () => requestConfirm('send_now');

// Pill tooltips — surface who acted, why, and when for settled states.
const getAgentById = useMapGetter('agents/getAgentById');

const agentNameFrom = attrKey => {
  const agentId = contentAttributes.value?.[attrKey];
  return agentId ? getAgentById.value(agentId)?.name : null;
};

const isoToReadableTime = iso => {
  if (!iso) return null;
  const seconds = Math.floor(new Date(iso).getTime() / 1000);
  return messageTimestamp(seconds, 'LLL d, h:mm a');
};
// Single lookup mirrors STATE_PILL_CLASSES/STATE_ICONS — timestampLabel resolves
// the right content-attribute for the current state (used inside state-matched
// tooltip branches below).
const TIMESTAMP_KEY_BY_STATE = {
  approved: 'approvedAt',
  rejected: 'rejectedAt',
  cancelled: 'cancelledAt',
  sent: 'sentAt',
  failed_window_closed: 'failedAt',
  failed_send_error: 'failedAt',
  failed_conversation_resolved: 'failedAt',
};
const timestampLabel = computed(() =>
  isoToReadableTime(
    contentAttributes.value?.[TIMESTAMP_KEY_BY_STATE[scheduleState.value]]
  )
);

const KNOWN_CANCEL_REASONS = [
  'cs_rejected',
  'expired',
  'superseded_by_incoming',
  'superseded_by_outgoing',
  'superseded_by_template',
  'cancelled_status_resolved',
];
const cancelReasonLabel = computed(() => {
  const raw = contentAttributes.value?.cancelReason;
  if (!raw) return null;
  return KNOWN_CANCEL_REASONS.includes(raw)
    ? t(`SCHEDULE_BUBBLE.CANCEL_REASON.${raw.toUpperCase()}`)
    : raw;
});

// Per-state tooltip recipe: who acted (agent attr + label key) and whether
// the cancel reason applies. The state timestamp is appended for all.
const ACTOR_TOOLTIPS = {
  approved: {
    agentKey: 'reviewedByAgentId',
    labelKey: 'SCHEDULE_BUBBLE.TOOLTIP.APPROVED_BY',
    showReason: false,
  },
  rejected: {
    agentKey: 'rejectedByAgentId',
    labelKey: 'SCHEDULE_BUBBLE.TOOLTIP.REJECTED_BY',
    showReason: true,
  },
  cancelled: {
    agentKey: 'cancelledByAgentId',
    labelKey: 'SCHEDULE_BUBBLE.TOOLTIP.CANCELLED_BY',
    showReason: true,
  },
};

const actorTooltip = ({ agentKey, labelKey, showReason }) => {
  const parts = [];
  const agent = agentNameFrom(agentKey);
  if (agent) parts.push(t(labelKey, { agent }));
  if (showReason && cancelReasonLabel.value) {
    parts.push(cancelReasonLabel.value);
  }
  if (timestampLabel.value) parts.push(timestampLabel.value);
  return parts.length ? parts.join(' · ') : null;
};

const pillTooltip = computed(() => {
  const actorConfig = ACTOR_TOOLTIPS[scheduleState.value];
  if (actorConfig) return actorTooltip(actorConfig);
  if (scheduleState.value === 'sent' && timestampLabel.value) {
    return t('SCHEDULE_BUBBLE.TOOLTIP.SENT_AT', { time: timestampLabel.value });
  }
  if (isFailure.value && timestampLabel.value) {
    return t('SCHEDULE_BUBBLE.TOOLTIP.FAILED_AT', {
      time: timestampLabel.value,
    });
  }
  return null;
});

// Per-state action visibility, driven by ScheduleMessages::TransitionService::ALLOWED.
// Untimed schedules (no scheduled_at) have nothing to approve for — Send now only.
const showApprove = computed(
  () =>
    !!scheduledAt.value && ['pending', 'edited'].includes(scheduleState.value)
);
const showRejectAndEdit = computed(() =>
  ['pending', 'edited', 'approved'].includes(scheduleState.value)
);
const showSendNow = computed(() =>
  ['pending', 'edited', 'approved'].includes(scheduleState.value)
);
</script>

<template>
  <BaseBubble
    class="px-4 py-3 min-w-[28rem]"
    data-bubble-name="schedule"
    data-test="schedule-bubble"
  >
    <!-- Header strip: contextual label on the left, state pill (with icon
         inside the pill) far right. Same shape across all states so the
         bubble doesn't shift between modes. -->
    <div class="mb-2 flex items-center gap-2 text-xs text-n-slate-11">
      <span v-if="editing">
        {{ t('SCHEDULE_BUBBLE.HEADER.PAUSED_FOR_EDITS') }}
      </span>
      <span
        v-else-if="!isTerminal && scheduledAt"
        data-test="schedule-countdown"
      >
        {{
          t('SCHEDULE_BUBBLE.HEADER.SENDS_IN', { remaining: countdownLabel })
        }}
      </span>
      <span
        v-tooltip.top="pillTooltip"
        class="ml-auto inline-flex items-center gap-1 rounded px-2 py-0.5"
        :class="statePillClass"
        data-test="schedule-state-badge"
      >
        <Icon :icon="stateIcon" class="size-3" />
        {{ t(`SCHEDULE_BUBBLE.STATE.${scheduleState.toUpperCase()}`) }}
      </span>
    </div>

    <!-- Screen-reader polite region for countdown (minute granularity) -->
    <span class="sr-only" aria-live="polite">
      {{ !isTerminal && !editing ? politeCountdownLabel : '' }}
    </span>

    <!-- Body -->
    <div
      v-if="!editing"
      class="prose prose-bubble whitespace-pre-wrap"
      data-test="schedule-text"
    >
      {{ displayText }}
    </div>
    <!-- Visually match the display div: no border, no extra padding, just an
         inline editable surface. A focus ring on the surrounding bubble
         signals editability without changing the bubble's footprint. -->
    <textarea
      v-else
      ref="textareaRef"
      v-model="editDraft"
      rows="1"
      :aria-label="t('SCHEDULE_BUBBLE.EDIT_ARIA_LABEL')"
      class="prose prose-bubble w-full whitespace-pre-wrap bg-transparent border-0 p-0 m-0 text-inherit focus:outline-none focus:ring-0 resize-none overflow-hidden"
      data-test="schedule-edit-textarea"
      @input="adjustTextareaHeight"
    />

    <!-- Failure reason -->
    <div
      v-if="isFailure && failureReasonKey"
      class="mt-2 text-xs text-n-ruby-11"
      data-test="schedule-failure-reason"
    >
      {{ t(failureReasonKey) }}
    </div>

    <!-- Action row -->
    <div v-if="!isTerminal" class="mt-3 flex flex-wrap items-center gap-2">
      <template v-if="!editing">
        <NextButton
          v-if="showRejectAndEdit"
          color="slate"
          size="sm"
          variant="faded"
          icon="i-lucide-pencil"
          :label="t('SCHEDULE_BUBBLE.EDIT')"
          :is-loading="busy"
          @click="startEdit"
        />
        <NextButton
          v-if="showRejectAndEdit"
          color="ruby"
          size="sm"
          variant="faded"
          icon="i-lucide-x"
          :label="t('SCHEDULE_BUBBLE.REJECT')"
          :is-loading="busy"
          @click="reject"
        />
        <NextButton
          v-if="showApprove"
          color="teal"
          size="sm"
          variant="solid"
          icon="i-lucide-check"
          class="ml-auto"
          :label="t('SCHEDULE_BUBBLE.APPROVE')"
          :is-loading="busy"
          @click="approve"
        />
        <NextButton
          v-if="showSendNow"
          color="blue"
          size="sm"
          variant="solid"
          icon="i-lucide-send"
          :class="{ 'ml-auto': !showApprove }"
          :label="t('SCHEDULE_BUBBLE.SEND_NOW')"
          :is-loading="busy"
          @click="sendNow"
        />
      </template>
      <template v-else>
        <NextButton
          color="slate"
          size="sm"
          variant="faded"
          icon="i-lucide-x"
          :label="t('SCHEDULE_BUBBLE.CANCEL_EDIT')"
          @click="cancelEdit"
        />
        <NextButton
          color="teal"
          size="sm"
          variant="solid"
          icon="i-lucide-check"
          class="ml-auto"
          :label="t('SCHEDULE_BUBBLE.SAVE')"
          :is-loading="busy"
          @click="saveEdit"
        />
      </template>
    </div>

    <Dialog
      ref="dialogRef"
      :type="confirmCopy.type"
      :title="confirmCopy.title"
      :description="confirmCopy.description"
      :confirm-button-label="confirmCopy.confirmLabel"
      :confirm-button-color="confirmCopy.confirmColor"
      :is-loading="busy"
      @confirm="onConfirm"
    />
  </BaseBubble>
</template>
